import { Alert, AppBar, Box, Button, ButtonGroup, Toolbar, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { selectUser } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../app/hooks";
import { ClassroomInfo, classroomSlice, ClassroomStatus, retrieveClassroom, selectClassroom } from "../../../redux/classroom/classroomSlice";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { retrieveSchool, schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { classroomStudentsSlice } from "../../../redux/classroom/classroomStudentsSlice";
import StudentsList from "./StudentsList";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import ClassroomPageTitle from "../basics/ClassroomPageTitle";
import ClassroomPageAvatar from "../basics/ClassroomPageAvatar";
import { classroomInsUpdSlice, classroomDelete } from "../../../redux/classroom/classroomInsUpdSlice";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import { imageInsSlice } from "../../../redux/image/imageInsSlice";
import MenuIcon from "@mui/icons-material/Menu";
import { LoadState } from "../../../redux/common";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { ObjectTarget, PERMISSIONS, PERMISSIONS_TO_ALLOW_CONVERSATION, PermissionId, checkIsAdmin, hasDirectPermission, hasPermission } from "../../../app/permissions";
import { routeClassroomEdit, routeClassroomImage, routeClassroomStudentAdd } from "../../../routes/classroom";
import { routeSchool } from "../../../routes/school";
import ClassroomUsersList from "./ClassroomUsersList";
import { usersForPermissionsSlice } from "../../../redux/user/usersForPermissionsSlice";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import { TopBar } from "../../../features/top/TopBar";
import { useEffect, useState } from "react";

import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import ShieldIcon from '@mui/icons-material/Shield';
import CollectionsIcon from "@mui/icons-material/Collections";
import MessageIcon from "@mui/icons-material/Message";
import EditIcon from "@mui/icons-material/Edit";
// import PollIcon from '@mui/icons-material/Poll';
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

import ClassroomImages from "./ClassroomImages";
import { ClassroomConversation } from "../conversation/ClassroomConversation";
import ClassroomRoutine from "./ClassroomRoutine";
import SurveyReport from "../../../features/survey/SurveyReport";
import TooltipButton from "../../../components/TooltipButton";
import ClassroomInviteReport from "./ClassroomInviteReport";
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ClassroomHomework from "./homework/HomeworkList";

const VALID_OPTIONS = [
    "main", "permissions", "routine", "images", "messages", "survey-report", "invite-report", "homework"
];

export type ClassroomContentOptions = typeof VALID_OPTIONS[number];

export function Classroom() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(selectUser);
    const school = useSelector(selectSchool);
    const classroom = useSelector(selectClassroom);
    const classroomAdmin = hasPermission(user.priza.info, PermissionId.CLASSROOM_ADMIN, classroom.selected?.id || "") 
        || hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, school.selected?.id || "");
    const classroomTeacher = classroomAdmin ||
        hasDirectPermission(user.priza.info, PermissionId.CLASSROOM_TEACHER, classroom.selected?.id || "");

    // Function to get initial tab from URL hash
    const getInitialTabFromHash = (): ClassroomContentOptions => {
        const hash = location.hash.replace('#', '');
        if (VALID_OPTIONS.includes(hash as ClassroomContentOptions)) {
            return hash as ClassroomContentOptions;
        }
        return "main";
    };

    const [contentSelected, setContentSelected] = useState<ClassroomContentOptions>(getInitialTabFromHash());

    const { schoolId, classroomId } = useParams();
    console.log(`SchoolId: ${schoolId}/${school.state.state}, ClassroomId: ${classroomId}/${classroom.state.state}`);

    useEffect(() => {
        if (school.selected && school.selected.id != schoolId) {
            dispatch(schoolSlice.actions.clean());
        }
        if (school.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchool({ schoolId: schoolId }));
        }
    }, [schoolId, school, user]);

    useEffect(() => {
        if (classroomId && classroom.selected && classroom.selected.id != classroomId) {
            dispatch(classroomSlice.actions.clean());
        }
        if (classroomId && classroom.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveClassroom({ classroomId: classroomId }));
        }    }, [classroomId, classroom, user]);

    // Listen for hash changes to update content selection
    useEffect(() => {
        const handleHashChange = () => {
            const newTab = getInitialTabFromHash();
            setContentSelected(newTab);
        };

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        
        // Also update on location change (for initial load or navigation)
        const newTab = getInitialTabFromHash();
        if (newTab !== contentSelected) {
            setContentSelected(newTab);
        }

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [location.hash, contentSelected]);

    if ((!schoolId) || (!classroomId)) {
        return <ElementMissingContext />;
    }    const addStudent = () => {
        navigate(routeClassroomStudentAdd(schoolId, classroomId));
    };

    // Function to handle tab change and update URL hash
    const handleTabChange = (tab: string) => {
        console.log(`handleTabChange: ${tab}`);
        const option = VALID_OPTIONS.includes(tab) ? tab as ClassroomContentOptions : "main";
        setContentSelected(option);
        window.location.hash = option;
    };

    const cleanup = () => {
        dispatch(schoolSlice.actions.clean());
        dispatch(classroomSlice.actions.clean());
        dispatch(classroomStudentsSlice.actions.clean());
        dispatch(usersForPermissionsSlice.actions.clean());
    };

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string|null>(null);

    const handleDeleteClick = () => setOpenDeleteDialog(true);
    const handleDeleteCancel = () => { setOpenDeleteDialog(false); setDeleteError(null); };
    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            await dispatch(classroomDelete({ classroomId: classroomId }) as any);
            setOpenDeleteDialog(false);
            navigate(routeSchool(schoolId, "classroom"));
        } catch (err: any) {
            setDeleteError("Erro ao remover sala de aula. Tente novamente.");
            setDeleting(false);
        }
    };

    const options: ContextMenuOptions[] = [];
    options.push({ label: "Atualizar", click: cleanup });
    if (classroomAdmin) {
        options.push({
            label: "Editar", click: () => {
                if (classroom.selected) {
                    const newClassroom: ClassroomInfo = { ...classroom.selected };
                    dispatch(classroomInsUpdSlice.actions.set(newClassroom));
                    navigate(routeClassroomEdit(schoolId, classroomId));
                }
            }
        });
        options.push({
            label: "Alterar Imagem", click: () => {
                if (classroom.selected) {
                    dispatch(imageInsSlice.actions.clean());
                    navigate(routeClassroomImage(schoolId, classroomId));
                }
            }
        });
        options.push({ label: "Adicionar Aluno", click: () => { addStudent(); } });
        options.push({ label: "Remover", click: handleDeleteClick });
    }
    const topMenu = <ContextMenu label={<MenuIcon />} options={options} />;

    const breadcrumbs = [
        {
            label: school.selected?.name ? school.selected?.name : "Escola",
            href: routeSchool(schoolId, "classroom")
        },
        {
            label: classroom.selected?.name || "Sala de aula",
        },
    ];

    const canDeleteImage = () => {
        if(hasPermission(user.priza.info, PermissionId.CLASSROOM_ADMIN, classroomId)) {
            return true;
        }
        if(hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)) {
            return true;
        }
        return false;
    };
    const canSendMessage = () => {
        for(const permission of PERMISSIONS_TO_ALLOW_CONVERSATION) {
            const perm = PERMISSIONS[permission];
            if (!perm) {
                continue;
            }
            if (perm.target != ObjectTarget.CLASSROOM) {
                continue;
            }
            if (hasDirectPermission(user.priza.info, permission, classroomId)) {
                return true;
            }
        }
        return false;
    };

    return <>
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} aria-labelledby="delete-classroom-dialog-title">
            <DialogTitle id="delete-classroom-dialog-title">Remover Sala de Aula</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Esta ação é <b>irreversível</b> e todos os dados associados serão perdidos!
                </DialogContentText>
                <DialogContentText sx={{ color: 'error.main' }}>
                    Tem certeza que deseja <b>REMOVER</b> a sala de aula <b>{classroom.selected?.name}</b>?
                </DialogContentText>
                {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} color="primary" variant="contained" disabled={deleting}>Cancelar</Button>
                <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting} autoFocus>
                    {deleting ? "Removendo..." : "Remover"}
                </Button>
            </DialogActions>
        </Dialog>
        <PageAuthenticatedLoading
        topbar={<TopBar
            topMenu={topMenu}
            title={
                <Typography variant="h6" component="div">
                    Sala de aula
                </Typography>
            }
            breadcrumbs={breadcrumbs}
        />}
        loading={{
            states: [school.state, classroom.state],
            content: () => {
                const body = () => {
                    if((!school.selected) || (!classroom.selected)) {
                        return <ElementMissingContext />;
                    }
                    const alert = classroom.selected?.classroomStatus === ClassroomStatus.DONE 
                        ? <Alert severity="error">
                            Esta sala de aula está finalizada.
                        </Alert>
                        : classroom.selected?.classroomStatus === ClassroomStatus.NOT_READY
                        ? <Alert severity="warning">
                            Esta sala de aula está com cadastro em andamento.
                        </Alert>
                        : null;
                    
                    switch (contentSelected) {
                        case "main": return <>
                                {alert}
                                <StudentsList schoolId={schoolId} classroomId={classroomId} />
                            </>;
                        case "routine": return <>
                                {alert}
                                <ClassroomRoutine school={school.selected} classroom={classroom.selected} />
                            </>;
                        case "permissions": return <>
                                {alert}
                                <ClassroomUsersList schoolId={schoolId} classroomId={classroomId} />
                            </>;
                        case "images": return <>
                                {alert}
                                <ClassroomImages schoolId={schoolId} classroomId={classroomId} canDeleteImage={canDeleteImage()} />
                            </>;
                        case "homework": return <>
                                {alert}
                                <ClassroomHomework school={school.selected} classroom={classroom.selected} />
                            </>;
                        case "messages": return <ClassroomConversation school={school.selected} classroom={classroom.selected} />;
                        case "invite-report": return <>
                                {alert}
                                <ClassroomInviteReport school={school.selected} classroom={classroom.selected} />
                            </>;
                        case "survey-report": return <>
                                {alert}
                                <SurveyReport target={ObjectTarget.CLASSROOM} targetId={classroomId} />
                            </>;
                    }
                };

                return <>
                    <AppBar key={"app-name"} id={"app-name"} position="static" color="default">
                        <Toolbar>
                            <ClassroomPageAvatar />
                            <ClassroomPageTitle schoolId={schoolId} classroomId={classroomId} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate(routeSchool(schoolId, "classroom"))}>Escola</Button>
                        </Toolbar>
                    </AppBar>
                    <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                        <Toolbar>
                            <ButtonGroup variant="contained" sx={{ mr: 2 }}>
                                <TooltipButton
                                    option="main"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Alunos">
                                    <PeopleAltIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="routine"
                                    optionSelected={contentSelected}
                                    disabled={!classroomTeacher}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Rotina">
                                    <RoomPreferencesIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="permissions"
                                    disabled={!classroomAdmin}
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Usuários">
                                    <ShieldIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="images"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Imagens">
                                    <CollectionsIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="messages"
                                    optionSelected={contentSelected}
                                    disabled={!canSendMessage()}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Mensagens">
                                    <MessageIcon />
                                </TooltipButton>
                                {/* <TooltipButton  
                                    option="survey-report" 
                                    optionSelected={contentSelected} 
                                    onClick={(option) => handleTabChange(option)} 
                                    disabled={!canSendMessage()}
                                    tooltip="Resultado de enquetes">
                                    <PollIcon />
                                </TooltipButton> */}
                                <TooltipButton
                                    option="homework"
                                    optionSelected={contentSelected}
                                    disabled={!classroomAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Lições de casa">
                                    <HomeWorkIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="invite-report"
                                    optionSelected={contentSelected}
                                    disabled={!classroomAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Convite para os pais">
                                    <AppRegistrationIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="edit-classroom"
                                    optionSelected={contentSelected}
                                    disabled={!classroomTeacher}
                                    onClick={() => {
                                            const newClassroom = { ...classroom.selected } as ClassroomInfo;
                                            dispatch(classroomInsUpdSlice.actions.set(newClassroom));
                                            navigate(routeClassroomEdit(schoolId, classroomId));
                                        }
                                    }
                                    tooltip="Editar Sala">
                                    <EditIcon />
                                </TooltipButton>
                            </ButtonGroup>
                        </Toolbar>
                    </AppBar>
                    {body()}
                </>;
            },
            error: "Erro ao carregar lista de estudantes",
            retry: cleanup
        }} />
    </>;
}
